import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ctorMap } from '../../apps';
import { useAppDefinitions } from '../../apps/definition';
import { UserApp } from '../../models/User';
import { ConnectionInfoPopover } from '../admin/AppsTableRow/AppsTableRow';
import { LinkFavicon } from '../controls/LinkFavicon/LinkFavicon';
import { UIIcon } from '../controls/ui/UIIcon/UIIcon';

interface IAppConnectionsSelectProps {
  apps: UserApp[];
  selectedApps: UserApp[];
  setSelectedApps: (apps: UserApp[]) => void;
}

export const AppConnectionsSelect: React.FC<IAppConnectionsSelectProps> = ({
  apps,
  selectedApps,
  setSelectedApps,
}) => {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const selectRef = useRef<HTMLDivElement>(null);
  const definitions = useAppDefinitions();

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        selectRef.current &&
        !selectRef.current.contains(event.target as Node)
      ) {
        setPopoverOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSelectAll = useCallback(() => {
    setSelectedApps(apps.map((app) => app));
  }, [apps, setSelectedApps]);

  const handleClearAll = useCallback(() => {
    setSelectedApps([]);
  }, [setSelectedApps]);

  return (
    <div
      className="inline-flex relative border border-solid border-gray-30 rounded-md mt-2 px-1.5 py-1 cursor-pointer gap-1"
      onClick={() => {
        setPopoverOpen(true);
      }}
    >
      {selectedApps.slice(0, 4).map((selectedApp) => {
        const matchingApp = apps.find((app) => app.id === selectedApp.id);
        if (matchingApp) {
          return (
            <UIIcon
              key={selectedApp.id}
              name={matchingApp.appName}
              size={16}
              type="apps"
            />
          );
        }

        return null;
      })}
      {selectedApps.length > 4 && (
        <div className="text-xs">+{selectedApps.length - 4}</div>
      )}
      {selectedApps.length === 0 && <UIIcon name="app" size={16} type="ui" />}

      <UIIcon className="selectChevron hover:pt-1" name="arrow-down" />
      {popoverOpen && (
        <div
          className="absolute top-5 left-0 bg-white border border-solid border-gray-30 rounded-md mt-2 min-w-[208px] max-h-[300px] overflow-y-auto !cursor"
          ref={selectRef}
        >
          <div className="flex gap-2 px-4 py-2 border-b text-sm">
            <div
              className={`${
                selectedApps.length === apps.length ? 'text-cloud-20' : 0
              }`}
              onClick={handleSelectAll}
            >
              Select All
            </div>
            <div>|</div>
            <div
              className={`${selectedApps.length === 0 ? 'text-cloud-20' : 0}`}
              onClick={handleClearAll}
            >
              Clear All
            </div>
          </div>
          {apps.map((app) => {
            const selected = selectedApps.find(
              (selectedApp) => selectedApp.id === app.id
            );

            const definition = definitions[app.appName];
            const AppConstructor = ctorMap.get(app.appName);

            let appCtor;
            if (definition && AppConstructor) {
              appCtor = new AppConstructor(definition);
            }

            return (
              <li
                className="flex items-center gap-2 justify-between p-2 hover:bg-gray-10 cursor-pointer"
                key={app.id}
                onClick={() => {
                  if (selected) {
                    setSelectedApps(
                      selectedApps.filter(
                        (selectedApp) => selectedApp.id !== app.id
                      )
                    );
                  } else {
                    setSelectedApps([...selectedApps, app]);
                  }
                }}
              >
                <div className="flex items-center gap-2 ">
                  {app.appName === 'website' ? (
                    <LinkFavicon
                      link={`http://${app.workspaceName!}`}
                      size={20}
                    />
                  ) : (
                    <UIIcon name={app.appName} type="apps" />
                  )}
                  <span>{app.connectionName || definition?.displayName}</span>
                  {appCtor && (
                    <ConnectionInfoPopover app={appCtor} connection={app} />
                  )}
                </div>
                <UIIcon
                  className={`${selected ? '' : 'invisible'}`}
                  name="check"
                />
              </li>
            );
          })}
        </div>
      )}
    </div>
  );
};
